import { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../config';
import PainelAnexosNegocio from '../components/PainelAnexosNegocio';

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

// L9 (2026-06-02): agora aceita templates E composicoes ja feitas como fonte
// pra clonar — antes so listava is_template=true.
interface Template {
  id: string;
  nome: string;
  is_template: boolean;
  n_itens: number;
  proposta_id?: number | null;
  proposta_titulo?: string | null;
}

// Modal de criação rápida de composição vinculada a um negócio.
// Pode partir de um template ou criar do zero.
// Não navega — cria e chama callback (que provavelmente abre o editor da composição).
export default function ModalNovaComposicao({ propostaId, tituloNegocio, onClose, onCriada }: {
  propostaId: number;
  tituloNegocio: string;
  onClose: () => void;
  onCriada: (composicaoId: string) => void;
}) {
  const [nome, setNome] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [markupPadrao, setMarkupPadrao] = useState('30');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [carregandoTpl, setCarregandoTpl] = useState(true);

  useEffect(() => {
    setNome(`Composição — ${tituloNegocio}`);
    (async () => {
      try {
        const token = await getToken();
        const [tplRes, mkpRes] = await Promise.all([
          axios.get(`${API_URL}/comercial/composicoes`, { headers: { Authorization: token } }),
          axios.get(`${API_URL}/markup-base`, { headers: { Authorization: token } }).catch(() => null),
        ]);
        const tpls = tplRes.data.body ? JSON.parse(tplRes.data.body) : tplRes.data;
        const mkp = mkpRes ? (mkpRes.data.body ? JSON.parse(mkpRes.data.body) : mkpRes.data) : null;
        // L9 (2026-06-02): inclui composicoes ja feitas em outras propostas.
        // Filtra a composicao da propria proposta atual (que ja existe) e
        // composicoes sem itens (vazias nao servem como base).
        setTemplates(Array.isArray(tpls)
          ? tpls.filter(c => (c.n_itens || 0) > 0 && c.proposta_id !== propostaId)
          : []);
        if (mkp?.acrescimo_pct && mkp.acrescimo_pct > 0) {
          setMarkupPadrao(mkp.acrescimo_pct.toFixed(2));
        }
      } finally { setCarregandoTpl(false); }
    })();
  }, [tituloNegocio]);

  async function criar() {
    if (!nome.trim()) { alert('Nome obrigatório'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      let res;
      if (templateId) {
        // Cria a partir de template (clona itens) + vincula à proposta atual
        res = await axios.post(`${API_URL}/comercial/composicoes/${templateId}/clonar`, {
          nome,
          proposta_id: propostaId,
          is_template: false,
        }, { headers: { Authorization: token } });
      } else {
        // Cria do zero
        res = await axios.post(`${API_URL}/comercial/composicoes`, {
          nome,
          proposta_id: propostaId,
          markup_padrao_pct: parseFloat(markupPadrao) || 30,
          is_template: false,
        }, { headers: { Authorization: token } });
      }
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      if (!data?.id) throw new Error('Resposta inválida do servidor');
      onCriada(data.id);
    } catch (e: any) {
      alert('Erro ao criar composição: ' + (e.response?.data?.error || e.response?.data?.erro || e.message));
    } finally { setSalvando(false); }
  }

  const inp: React.CSSProperties = {
    width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 5,
    border: '1px solid #ddd', boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1500,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 12, width: '92%', maxWidth: 820,
        display: 'flex', maxHeight: '88vh', overflow: 'hidden', position: 'relative',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ flex: 1, padding: 24, overflowY: 'auto' }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#020D7F' }}>
            📊 Nova composição
          </h3>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 14 }}>
            Vinculada ao negócio: <strong>{tituloNegocio}</strong>
          </div>

          {carregandoTpl && (
            <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 12 }}>
              Carregando templates e markup base...
            </div>
          )}

          {!carregandoTpl && (
            <>
          {templates.length > 0 && (
            <div style={{ marginBottom: 12, padding: 10, background: '#FFF8E8', border: '0.5px solid #B07500', borderRadius: 6 }}>
              <label style={{ ...lbl, color: '#B07500' }}>📋 Partir de uma composição existente (opcional)</label>
              <select value={templateId} onChange={e => setTemplateId(e.target.value)} style={inp}>
                <option value="">Começar do zero</option>
                {/* L9: agrupado — templates primeiro, depois composicoes ja feitas */}
                {templates.some(t => t.is_template) && (
                  <optgroup label="📋 Templates">
                    {templates.filter(t => t.is_template).map(t => (
                      <option key={t.id} value={t.id}>{t.nome} ({t.n_itens} itens)</option>
                    ))}
                  </optgroup>
                )}
                {templates.some(t => !t.is_template) && (
                  <optgroup label="📦 Composições existentes">
                    {templates.filter(t => !t.is_template).map(t => (
                      <option key={t.id} value={t.id}>
                        {t.nome} ({t.n_itens} itens){t.proposta_titulo ? ` — ${t.proposta_titulo}` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                Copia todos os itens da composição base. Você ajusta quantidades depois.
              </div>
            </div>
          )}

          <label style={lbl}>Nome *</label>
          <input value={nome} autoFocus onChange={e => setNome(e.target.value)} style={inp} />

          {!templateId && (
            <>
              <label style={{ ...lbl, marginTop: 12 }}>Acréscimo padrão (%)</label>
              <input type="number" step="0.01" value={markupPadrao}
                onChange={e => setMarkupPadrao(e.target.value)} style={inp} />
              <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>
                Pré-preenchido com o markup calculado da empresa. Ajustável dentro da composição.
              </div>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <button onClick={onClose}
              style={{ padding: '8px 18px', fontSize: 12, border: '1px solid #ddd', borderRadius: 5, background: '#fff', cursor: 'pointer' }}>
              Cancelar
            </button>
            <button onClick={criar} disabled={salvando}
              style={{ padding: '8px 22px', fontSize: 12, border: 'none', borderRadius: 5,
                background: '#0930D0', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
              {salvando ? 'Criando...' : (templateId ? 'Criar a partir da composição selecionada' : 'Criar composição')}
            </button>
          </div>
            </>
          )}
        </div>
        <PainelAnexosNegocio negocioId={propostaId} />
      </div>
    </div>
  );
}
